import { Button, Card, CardBody, CardHeader, Divider, Input, Spinner, Switch } from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useSWR from "swr";
import { getFetcher, patchFetcher } from "../../../../services/api";
import { useNavigate, useParams } from "react-router-dom";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import { yupResolver } from "@hookform/resolvers/yup";
import { month } from "../../../../enums/month";
import toast from "react-hot-toast";
import { isResponseErrorObject } from "../../../../services/helpers";
import DynamicImportData from "../../../../components/fragments/admin/report/purchase/DynamicImportData";
import { reportPurchaseValidation } from "../../../../services/validation/report-purchase.validation";
import ControlledReactSelect from "../../../../components/elements/ControlledReactSelect";
import ControlledInput from "../../../../components/elements/ControlledInput";
import InlineEditSupplier from "../../../../components/fragments/admin/report/purchase/InlineEditSupplier";
import { format } from "date-fns";

export default function EditPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isExistReport, setIsExistReport] = useState(true);
  const { data, isLoading } = useSWR(`/api/pelaporan-pembelian/${id}`, getFetcher);
  const {
    register,
    reset,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(reportPurchaseValidation) });
  const { data: dataCountry, isLoading: isLoadingCountry } = useSWR(`/api/countries`, getFetcher);
  const { data: dataTransporter, isLoading: isLoadingTransporter, mutate: mutateTransporter } = useSWR(data ? `/api/company/search-data-transporter?companyId=${data?.company?.id}` : null, getFetcher);

  useEffect(() => {
    if (!isLoadingTransporter && !isLoading) {
      reset({
        laporanTersedia: data?.laporanTersedia,
        tipePembelian: data?.tipePembelian,
        negaraAsal: data?.tipePembelian === "Import" ? data?.registrasi?.BahanB3Registrasi[0].asal_negara : undefined,
        dataSuppliers: data?.tipePembelian === "Lokal" ? data?.DataSupplierOnPelaporanPembelianBahanB3?.map((supplier) => ({ supplierId: supplier.dataSupplierId, jumlahPembelianB3: supplier.jumlahBahanB3Dibeli })) : undefined,
        dataImport:
          data?.tipePembelian === "Import"
            ? data?.DataImportOnPelaporanPembelianBahanB3?.map((importData) => ({
                pelabuhanBongkarId: importData.pelabuhanBongkar.id,
                dataTransporters: importData.DataTransporterOnPelaporanPembelianImportBahanB3.map((transporter) => transporter.dataTransporter.id),
                tanggalImport: format(importData.tanggalSuratPdb, "yyyy-MM-dd"),
                jumlahBahanB3DiBeli: importData.jumlahBahanB3Dibeli,
              }))
            : undefined,
      });
    }
  }, [data, isLoading, isLoadingTransporter, reset]);

  function existReport(val) {
    setIsExistReport(val);
    setValue("jumlahPembelianB3", 0);
    setValue("dataImport", undefined);
    setValue("dataSuppliers", undefined);
  }

  async function onSubmitForm(formData) {
    try {
      await patchFetcher(`/api/pelaporan-pembelian/update`, id, formData);
      toast.success("Laporan pembelian berhasil diperbarui");
    } catch (error) {
      isResponseErrorObject(error.response.data.message)
        ? Object.entries(error.response.data.message).forEach(([key, value]) => {
            toast.error(value);
          })
        : toast.error(error.response.data.message);
    }
  }

  return (
    <RootAdmin>
      <Card radius="sm" className="mt-3">
        <CardHeader>Ubah Pembelian Jenis B3 </CardHeader>
        <Divider />
        <CardBody>
          <form onSubmit={handleSubmit(onSubmitForm)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              <Input isRequired isDisabled label="Perusahaan" labelPlacement="outside" variant="faded" value={data?.company?.name} />
              <Input isRequired isDisabled label="Periode" labelPlacement="outside" variant="faded" value={data?.period?.name} />
              <Input isRequired isDisabled label="Bulan" labelPlacement="outside" variant="faded" value={month[data?.bulan - 1]} />
              <Input isRequired isDisabled label="Tahun" labelPlacement="outside" variant="faded" value={data?.tahun} />
              <ControlledInput control={control} name="tipePembelian" label="Tipe Pembelian" isRequired={true} isDisabled={true} />
              <Input isRequired isDisabled label="Jenis B3" labelPlacement="outside" variant="faded" value={`${data?.dataBahanB3?.casNumber} - ${data?.dataBahanB3?.namaBahanKimia}`} />
              {data?.tipePembelian === "Import" && (
                <>
                  <Input isRequired isDisabled label="Registrasi ID" labelPlacement="outside" variant="faded" value={data?.registrasi?.nomor_surat_formatted ?? data?.registrasi?.nomor_surat} />
                  <ControlledReactSelect control={control} isLoading={isLoadingCountry} label="Negara Asal" name="negaraAsal" isMulti options={dataCountry?.map((item) => ({ value: item.code2, label: item.name }))} isDisabled />
                </>
              )}
              <div className="col-span-2 mt-3">
                <Switch
                  {...register("laporanTersedia", {
                    onChange: (e) => {
                      existReport(e.target.checked);
                    },
                  })}
                  size="sm"
                  isSelected={isExistReport}
                >
                  {isExistReport ? "Laporan Tersedia" : "Tidak ada laporan pembelian"}
                </Switch>
              </div>
              {isExistReport &&
                (data?.tipePembelian === "Import" ? (
                  <div className="col-span-2">
                    <DynamicImportData
                      control={control}
                      setValue={setValue}
                      errors={errors}
                      dataRegistration={data?.registrasi}
                      selectedCompany={data?.company?.id}
                      dataTransporter={dataTransporter}
                      isLoadingTransporter={isLoadingTransporter}
                      mutateTransporter={mutateTransporter}
                    />
                  </div>
                ) : (
                  <div className="col-span-2">
                    <InlineEditSupplier control={control} selectedCompany={data?.company?.id} setValue={setValue} />
                  </div>
                ))}
            </div>
            <div className="flex items-center gap-1">
              <Button isLoading={isSubmitting} isDisabled={isSubmitting} type="submit" color="primary">
                Simpan
              </Button>
              <Button isDisabled={isSubmitting} color="danger" variant="faded" onPress={() => navigate(-1)}>
                Kembali
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </RootAdmin>
  );
}
